/**
 * Simbolo de codigo de barras.
 *
 * Dibuja el codigo en SVG respetando las proporciones de la norma, que es lo
 * que decide si un lector lo lee o no cuando esta impreso en papel:
 *
 * - ZONAS DE SILENCIO. El margen blanco a los lados no es decoracion, es parte
 *   del simbolo. El lector lo usa para saber donde empieza el codigo. Recortarlo
 *   para que la etiqueta quede mas compacta es la causa numero uno de que una
 *   etiqueta casera no se deje escanear.
 * - BARRAS GUARDA MAS LARGAS. En EAN, las de los extremos y la del centro bajan
 *   mas que las demas; el lector se apoya en ellas para sincronizarse. Code 128
 *   no las tiene: todas sus barras miden lo mismo.
 * - TAMAÑO FISICO REAL. El ancho se fija en MILIMETROS, no en pixeles. Un SVG
 *   medido en pixeles sale de un tamaño distinto en cada impresora, y por debajo
 *   del 80 % de magnificacion los modulos dejan de resolverse.
 *
 * Dibuja lo que `barcodePattern` decida: EAN-13/8 y UPC-A para el producto de
 * tienda, y Code 128 para todo lo demas —un codigo alfanumerico de proveedor,
 * un identificador propio—. El componente no sabe de simbologias mas alla de
 * donde poner las zonas de silencio y el texto; la codificacion vive en
 * `@abarrotes/shared`.
 *
 * En pantalla sirve para verificar; impreso, para pegarlo en el producto.
 */

import type { CSSProperties, ReactNode } from 'react';
import { BARCODE_GEOMETRY, barcodePattern, isPrintable } from '@abarrotes/shared';
import { cx } from './ui.js';

interface BarcodeProps {
  value: string;
  /**
   * Magnificacion respecto al tamaño nominal. 1 = 100 % (37.29 mm de ancho en
   * EAN-13). La norma admite de 0.8 a 2.0; por debajo de 0.8 el simbolo deja de
   * ser fiable en una impresora domestica.
   */
  magnification?: number;
  showText?: boolean;
  className?: string;
}

export function Barcode({
  value,
  magnification = 1,
  showText = true,
  className,
}: BarcodeProps): ReactNode {
  if (!isPrintable(value)) {
    return (
      <div
        className={cx(
          'inline-flex items-center justify-center rounded-lg border border-dashed border-line',
          'px-4 py-3 text-[12px] text-subtle text-center',
          className,
        )}
      >
        Este codigo tiene caracteres que no caben en un
        <br />
        codigo de barras. Sirve para buscarlo tecleando.
      </div>
    );
  }

  const pattern = barcodePattern(value);
  const isEan13 = pattern.symbology === 'ean13';
  const isCode128 = pattern.symbology === 'code128';

  // Code 128 exige mas silencio a los lados; EAN-13 reparte 11 y 7, EAN-8 pone 7.
  const quietLeft = isCode128
    ? BARCODE_GEOMETRY.quietCode128
    : isEan13
      ? BARCODE_GEOMETRY.quietLeftEan13
      : BARCODE_GEOMETRY.quietEan8;
  const quietRight = isCode128
    ? BARCODE_GEOMETRY.quietCode128
    : isEan13
      ? BARCODE_GEOMETRY.quietRightEan13
      : BARCODE_GEOMETRY.quietEan8;

  const symbolModules = pattern.modules.length;
  const totalModules = quietLeft + symbolModules + quietRight;

  const barHeight = BARCODE_GEOMETRY.barModules;
  const guardHeight = barHeight + BARCODE_GEOMETRY.guardExtraModules;
  const textHeight = showText ? 11 : 0;
  const totalHeight = guardHeight + textHeight;

  const clamped = Math.max(BARCODE_GEOMETRY.minMagnification, magnification);
  const widthMm = totalModules * BARCODE_GEOMETRY.moduleMm * clamped;
  // El alto NO se declara: sale de `aspectRatio` mas abajo, que es lo que
  // permite que el simbolo se encoja en pantalla sin deformarse. Con el alto
  // fijo en milimetros y el ancho encogiendose, las barras se estirarian.

  const isGuard = (index: number): boolean =>
    pattern.guardRanges.some((r) => index >= r.start && index < r.end);

  // Se agrupan los modulos contiguos en una sola barra: menos elementos en el
  // SVG y bordes limpios, sin costuras entre rectangulos pegados.
  const bars: { x: number; width: number; height: number }[] = [];
  let run = 0;

  for (let i = 0; i <= symbolModules; i++) {
    const isBar = i < symbolModules && pattern.modules.charAt(i) === '1';
    if (isBar) {
      run += 1;
      continue;
    }
    if (run > 0) {
      const start = i - run;
      bars.push({
        x: quietLeft + start,
        width: run,
        // Code 128 no diferencia barras: todas van a la altura larga.
        height: isCode128 || isGuard(start) ? guardHeight : barHeight,
      });
      run = 0;
    }
  }

  const fontSize = 9;
  const textY = guardHeight + 9;

  /*
   * Tamaño fisico al imprimir, encogible en pantalla.
   *
   * El ancho va en milimetros porque al IMPRIMIR es lo unico que importa: un
   * simbolo medido en pixeles sale de distinto tamaño en cada impresora, y por
   * debajo del 80 % de magnificacion los modulos dejan de resolverse.
   *
   * Pero en PANTALLA un ancho en milimetros no se puede encoger, y eso causaba
   * un daño que no se veia venir: una celda de rejilla mide por omision al menos
   * lo que mide su contenido, asi que el codigo le imponia su ancho fisico a
   * toda la columna que lo contuviera. En un telefono de 320 px el capitulo del
   * recorrido acababa midiendo 330 y el carril —que recorta el eje X a
   * proposito— se comia la diferencia: titulo y texto cortados a la derecha, sin
   * un solo error en consola.
   *
   * `maxWidth: 100%` con `height: auto` lo arregla sin tocar la impresion, y la
   * pieza que lo hace exacto es `aspectRatio`: como `widthMm` y `heightMm` salen
   * del mismo tamaño de modulo, su proporcion ES `totalModules / totalHeight`.
   * Mientras quepa, el alto calculado coincide al milimetro con el de antes; solo
   * cuando el contenedor es mas angosto que el simbolo, este se reduce en
   * proporcion. En pantalla eso es correcto: ahi el codigo se verifica con la
   * vista, no se escanea con un lector.
   */
  const sizing: CSSProperties = {
    width: `${widthMm}mm`,
    aspectRatio: `${totalModules} / ${totalHeight}`,
    height: 'auto',
    maxWidth: '100%',
  };

  return (
    <svg
      viewBox={`0 0 ${totalModules} ${totalHeight}`}
      style={sizing}
      className={cx('block', className)}
      role="img"
      aria-label={`Codigo de barras ${value}`}
      shapeRendering="crispEdges"
    >
      {/* El fondo blanco es obligatorio: las zonas de silencio tienen que ser
          blancas de verdad, no transparentes sobre un papel de color o sobre
          el fondo oscuro de la aplicacion. */}
      <rect x="0" y="0" width={totalModules} height={totalHeight} fill="#ffffff" />

      {bars.map((bar, index) => (
        <rect
          key={index}
          x={bar.x}
          y={0}
          width={bar.width}
          height={bar.height}
          fill="#000000"
        />
      ))}

      {showText ? (
        <g fill="#000000" fontFamily="'Courier New', monospace" fontSize={fontSize}>
          {isCode128 ? (
            // Code 128 lleva su linea legible entera y centrada bajo el simbolo.
            <text x={totalModules / 2} y={textY} textAnchor="middle" letterSpacing="1">
              {pattern.humanText}
            </text>
          ) : (
            <>
              {/* El primer digito del EAN-13 va FUERA del simbolo, en la zona de
                  silencio izquierda: no esta codificado en barras sino en la
                  paridad de las seis siguientes. */}
              {pattern.text.leading ? (
                <text x={0} y={textY} textAnchor="start">
                  {pattern.text.leading}
                </text>
              ) : null}

              <text
                x={quietLeft + (isEan13 ? 3 + 21 : 3 + 14)}
                y={textY}
                textAnchor="middle"
                letterSpacing="1"
              >
                {pattern.text.left}
              </text>

              <text
                x={quietLeft + (isEan13 ? 50 + 21 : 36 + 14)}
                y={textY}
                textAnchor="middle"
                letterSpacing="1"
              >
                {pattern.text.right}
              </text>
            </>
          )}
        </g>
      ) : null}
    </svg>
  );
}

/** Ancho fisico que ocupara el simbolo, para avisar si no cabe en la etiqueta. */
export function barcodeWidthMm(value: string, magnification = 1): number {
  if (!isPrintable(value)) return 0;
  const pattern = barcodePattern(value);
  const quiet =
    pattern.symbology === 'ean13'
      ? BARCODE_GEOMETRY.quietLeftEan13 + BARCODE_GEOMETRY.quietRightEan13
      : pattern.symbology === 'code128'
        ? BARCODE_GEOMETRY.quietCode128 * 2
        : BARCODE_GEOMETRY.quietEan8 * 2;
  return (
    (pattern.modules.length + quiet) *
    BARCODE_GEOMETRY.moduleMm *
    Math.max(BARCODE_GEOMETRY.minMagnification, magnification)
  );
}
